import {
  Modal,
  Box,
  Typography,
  IconButton,
  Stack,
  Grid,
  Button,
} from "@mui/material";
import CustomTextField from "../../../../../components/common/Textfield";
import { useEffect, useState } from "react";
import { IComment } from "../../../../../types/comment";
import CloseIcon from "@mui/icons-material/Close";
import PhoneNumberField from "../../../../../components/common/PhoneNumberField";
import apiService from "../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../components/common/ShowToast";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../..//store/Index";

interface AddContactModalProps {
  comment: IComment;
  setAddContactModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AddContactModal: React.FC<AddContactModalProps> = ({
  comment,
  setAddContactModalOpen,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [email, setEmail] = useState<string>(comment.authorEmail || "");
  const [emailError, setEmailError] = useState<string>("");
  const [firstName, setFirstName] = useState<string>(
    comment.author.split(" ")[0] || ""
  );
  const [lastName, setLastName] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    getLastname(comment.author);
  }, []);

  const validateEmail = (email: string): string => {
    if (!email) return "";
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!regex.test(email)) return "Invalid email format.";
    if (email.startsWith(".")) return "Email cannot start with a dot.";
    if (email.endsWith(".")) return "Email cannot end with a dot.";
    if (email.split("@")[0].endsWith("."))
      return "Local part cannot end with a dot.";
    return "";
  };

  const getLastname = (name: string) => {
    const splittedName = name.split(" ");
    console.log(splittedName);

    if (splittedName.length > 1) {
      setLastName(splittedName.at(-1) ?? "");
    }
  };

  const handleEmailChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const error = validateEmail(e.target.value);
    setEmailError(error);
    setEmail(e.target.value);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    if (!firstName || !lastName) {
      showErrorToast("First name and last name are required.");
      setIsSubmitting(false);
      return;
    }

    if (!email && !phoneNumber) {
      showErrorToast(
        "Provide at least one contact method (email or phone number)."
      );
      setIsSubmitting(false);
      return;
    }

    if (emailError && email) {
      showErrorToast("Please provide a valid email address");
      return;
    }

    const formData = new FormData();
    formData.append("contact_type", "individual");
    formData.append("first_name", firstName);
    formData.append("last_name", lastName);
    email
      ? formData.append("email", email)
      : phoneNumber && formData.append("phone", phoneNumber);

    try {
      await apiService.post<any>("/api/contacts/contact/", formData, false);

      showSuccessToast("Individual contact added successfully.");
    } catch (err: any) {
      showErrorToast("Failed to add contact,please try again");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={true}
      onClose={() => setAddContactModalOpen(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          bgcolor: "background.paper",
          border: "2px solid #fff",
          boxShadow: 24,
          borderRadius: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: ".8em 2em",
            borderBottom: `1px solid ${reduxTheme.primary_colors[300]}`,
          }}
        >
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Add Contact
          </Typography>

          <IconButton sx={{ "&:hover": { color: "#a60b0bff" } }}>
            <CloseIcon onClick={() => setAddContactModalOpen(false)} />
          </IconButton>
        </Box>

        <Stack spacing={2} sx={{ padding: "0.8em 2em", mt: 2 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
          >
            <CustomTextField
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
              label="Firsname"
              variant="outlined"
              required
            />

            <CustomTextField
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              label="Surname"
              variant="outlined"
              required
            />
          </Box>
          <CustomTextField
            value={email}
            onChange={(e) => handleEmailChange(e)}
            label="Email"
            variant="outlined"
          />
          <PhoneNumberField
            fullWidth
            label="Phone Number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e)}
            defaultCountry="ZW"
            variant="outlined"
            sx={{ marginBottom: "15px" }}
          />
        </Stack>

        <Grid
          container
          spacing={1}
          justifyContent={"end"}
          sx={{ mt: 2, paddingInline: "2em", mb: 3 }}
        >
          <Grid>
            <Button
              variant="outlined"
              onClick={() => setAddContactModalOpen(false)}
            >
              Cancel
            </Button>
          </Grid>

          <Grid>
            <Button variant="contained" onClick={handleSubmit}>
              Add Contact
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};
